import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Search, Building2, SortAsc } from 'lucide-react';
import { toast } from 'sonner';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase/client';
import { CreateCompanyDialog } from './company/CreateCompanyDialog';
import { CompanyList } from './company/CompanyList';
import { CompanyUsersDialog } from './company/CompanyUsersDialog';

interface Company {
  id: string;
  companyName: string;
  subdomain: string;
  contactEmail: string;
  contactName: string;
  phone?: string;
  primaryColor?: string;
  logo?: string;
  wellCount?: number;
  userCount?: number;
  status: 'active' | 'suspended';
  createdAt: string;
}

export default function CompanyManager({ onCompanyUpdate }: { onCompanyUpdate?: () => void }) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [viewingUsersFor, setViewingUsersFor] = useState<{ id: string, name: string } | null>(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const companiesRef = collection(db, 'companies');
      const snapshot = await getDocs(companiesRef);

      const companiesData = snapshot.docs.map(doc => ({
        id: doc.id,
        companyName: doc.data().companyName || '',
        subdomain: doc.data().subdomain || '',
        contactEmail: doc.data().contactEmail || '',
        contactName: doc.data().contactName || '',
        phone: doc.data().phone || '',
        primaryColor: doc.data().primaryColor || '#3B82F6',
        secondaryColor: doc.data().secondaryColor || '#10B981',
        logo: doc.data().logo || '',
        wellCount: doc.data().wellCount || 0,
        userCount: doc.data().userCount || 0,
        status: doc.data().status || 'active',
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      })) as Company[];

      // Sort by createdAt descending
      companiesData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      setCompanies(companiesData);
    } catch (error) {
      console.error('Error loading companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = () => {
    loadCompanies();
    if (onCompanyUpdate) onCompanyUpdate();
  };

  const handleDelete = async (companyId: string) => {
    if (!window.confirm('Are you sure you want to delete this company? This action cannot be undone.')) return;
    // In a real app, we'd probably soft delete or require more checks.
    // For now, let's just toast that it's implemented for safety
    toast.info('Delete functionality is restricted for safety.');
  };

  const filteredCompanies = companies.filter(company =>
    company.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.subdomain.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.contactEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search companies, domains, emails..."
            className="pl-9 bg-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          Provision Company
        </Button>
      </div>

      {/* Grid */}
      <CompanyList
        companies={filteredCompanies}
        isLoading={loading}
        onEdit={(c) => toast.info(`Editing ${c.companyName} is coming soon`)}
        onViewUsers={(id) => {
          const company = companies.find(c => c.id === id);
          if (company) setViewingUsersFor({ id: company.subdomain, name: company.companyName });
        }}
        onDelete={handleDelete}
      />

      {/* Modals */}
      <CreateCompanyDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCompanyCreated={handleCreateSuccess}
      />

      <CompanyUsersDialog
        isOpen={!!viewingUsersFor}
        onClose={() => setViewingUsersFor(null)}
        companyId={viewingUsersFor?.id || ''}
        companyName={viewingUsersFor?.name || ''}
      />
    </div>
  );
}